import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import type { NavigateFunction } from 'react-router-dom';
import {
  captureElementPngBlob,
  settleCaptureRoot,
  wait,
} from './captureUtils';
import { useGalleryStore } from './galleryStore';
import { galleryScreens } from './screens';

export type ExportProgress = {
  current: number;
  total: number;
  screenId: string | null;
};

type ExportOptions = {
  navigate: NavigateFunction;
  onProgress?: (progress: ExportProgress) => void;
};

const padIndex = (index: number) => String(index + 1).padStart(2, '0');

/**
 * Navigates through the current sequence, captures each `.gallery-screen`
 * at the fixed export frame, and downloads a ZIP of PNGs.
 */
export const exportSequenceAsZip = async ({
  navigate,
  onProgress,
}: ExportOptions): Promise<void> => {
  const { sequence, setExporting } = useGalleryStore.getState();
  if (sequence.length === 0) return;

  const returnPath = window.location.pathname;
  const zip = new JSZip();

  setExporting(true);
  // Let React apply the exporting frame + html class before first capture.
  await wait(120);

  try {
    for (let index = 0; index < sequence.length; index += 1) {
      const screenId = sequence[index];
      const screen = galleryScreens.find((entry) => entry.id === screenId);
      if (!screen) continue;

      onProgress?.({
        current: index + 1,
        total: sequence.length,
        screenId,
      });

      navigate(screen.route);
      await wait(450);
      await document.fonts.ready;

      const target = document.querySelector<HTMLElement>('.gallery-screen');
      if (!target) {
        throw new Error(`Could not find capture root for ${screenId}.`);
      }

      await settleCaptureRoot(target);
      const blob = await captureElementPngBlob(target, 2);
      zip.file(`${padIndex(index)}-${screen.id}.png`, blob);
    }

    const archive = await zip.generateAsync({ type: 'blob' });
    saveAs(archive, 'screenbook-export.zip');
  } finally {
    setExporting(false);
    navigate(returnPath);
    onProgress?.({ current: 0, total: 0, screenId: null });
  }
};
